import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { getArticle } from "@/lib/api";
import { useSEO, breadcrumbSchema } from "@/lib/seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal } from "@/components/motion/Reveal";
import NotFound from "@/pages/NotFound";

export default function Article() {
  const { slug } = useParams();
  const { data: article, isLoading, isError } = useQuery({
    queryKey: ["article", slug],
    queryFn: () => getArticle(slug),
    retry: false,
  });

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useSEO({
    title: article ? `${article.title} | Renova Research Resources` : "Article | Renova Peptide Co.",
    description: article?.excerpt || "",
    path: `/research-resources/${slug}`,
    type: "article",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: { "@type": "Organization", name: "Renova Peptide Co." },
            publisher: { "@type": "Organization", name: "Renova Peptide Co.", logo: { "@type": "ImageObject", url: `${origin}/assets/logo.png` } },
          },
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Research Resources", path: "/research-resources" },
            { name: article.title, path: `/research-resources/${slug}` },
          ]),
        ]
      : null,
  });

  if (isError) return <NotFound />;
  if (isLoading || !article) return <div className="container-rnv py-32 text-center text-[#85827D]">Loading…</div>;

  return (
    <article className="bg-white">
      <div className="border-b border-[#1E2A44]/8">
        <div className="container-rnv py-6">
          <Breadcrumbs items={[
            { name: "Home", path: "/" },
            { name: "Research Resources", path: "/research-resources" },
            { name: article.title, path: `/research-resources/${slug}` },
          ]} />
        </div>
      </div>

      <div className="container-rnv py-14 md:py-20">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="overline">{article.category}</span>
              <span className="text-xs text-[#85827D]">· {article.read_time}</span>
            </div>
            <h1 className="font-serif font-light text-4xl md:text-5xl text-[#1E2A44] mt-4 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-sm text-[#85827D]">Published {article.date}</p>
            <div className="gold-rule my-10" />
            <div className="space-y-6">
              {article.body.map((para, i) => (
                <p key={i} className={`leading-relaxed ${i === article.body.length - 1 ? "text-sm text-[#85827D] italic border-l-2 border-[#C8A96A] pl-5" : "text-[#262E3D] text-lg"}`}>
                  {para}
                </p>
              ))}
            </div>
            <div className="mt-12">
              <Link to="/research-resources" className="inline-flex items-center gap-2 text-sm text-[#1E2A44] link-underline font-medium">
                <ArrowLeft size={15} /> Back to Research Resources
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </article>
  );
}
